import {
  type CanvasElementFrom,
  type PropsOf,
  type WithProps,
  type WithSlots,
} from '@blaze-runtime/engine-domain';
import {
  type PropertiesConfig,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
} from '@blaze-shared/elements-domain';

import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { rootV2Migration } from './migrations/002-root.migration';
import { RootComponent } from './root.component';
import {
  type ROOT_BOTTOM_SLOT,
  type ROOT_HEADER_SLOT,
  ROOT_MAIN_SLOT,
  type ROOT_SIDE_SLOT,
} from './root.models';

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    root: RootDef;
  }
}

export type RootDef = WithSlots<
  typeof ROOT_HEADER_SLOT | typeof ROOT_MAIN_SLOT | typeof ROOT_SIDE_SLOT | typeof ROOT_BOTTOM_SLOT
> &
  WithProps<RootPropsModel>;
export type RootElement = CanvasElementFrom<'root'>;

registerElementDefinition({
  type: 'root',
  componentType: RootComponent,
  isListed: false,
  name: 'Page',
  width: { mode: 'full' },
  height: { mode: 'full' },
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  slots: {
    ids: [ROOT_MAIN_SLOT],
    [ROOT_MAIN_SLOT]: {
      children: [],
      sizing: {
        mode: 'auto',
        width: undefined,
      },
    },
  },
  features: {
    hideEmptySlots: true,
  },
  styleIncludePaths: ['style'],
  version: 2,
  migrations: {
    2: rootV2Migration,
  },
});

export type RootPropsModel = {
  style: {
    container: {
      fill: PropertiesConfigInputColorModel;
    } & {
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
    };
  };
};

function getDefaults(): PropsOf<'root'> {
  return {
    style: {
      container: {
        offset: { padding: '$t.spacingExternal' },
        fill: { color1: '$t.canvas', selectorType: 'solid' },
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('root', [
    epc.section('style', 'Style', [
      epc.subsections.styleSheetSubsection(),
      epc.subsections.subsection('style.container', 'Padding', [
        epc.fields.spacingAndAlignField('style.container.offset', { hasPadding: true }),
      ]),
      epc.subsections.elementBackgroundSubsection('style.container', 'Appearance', {
        allowGradientFill: false,
        allowNoneFill: false,
        allowNoneOutline: false,
        fillLabel: 'Fill',
        hasOutline: false,
        hasRadius: false,
        hasWidth: false,
        hideEffectPanel: true,
      }),
    ]),
  ]);
}
